/**
 * SDK 券包详情查询（同源 BFF 调用）
 *
 * 券包详情查询为公开端点（无需登录），但需 X-API-Key（服务端持有），SDK 浏览器侧无密钥，
 * 故走 hotel-hub 同源 BFF /api/coupons/group-detail（BFF 持密钥服务端代理）。
 * 用 raw fetch 同源 BFF（独立于 sdk/api/client.ts，逻辑与 customer-service.ts 一致）。
 *
 * 用于「领红包 banner 存在性校验 + 取 Banner 图」：调用 coupon-group-detail 端点一次拿全
 * 券包基本信息（Banner/头图）+ coupons 嵌入券列表。CouponBanner 据 coupons 非空判定是否展示，
 * 并从 banner_mobile_url / banner_pc_url 取 Banner 图（替代原先写死图）。不传 phone
 * （只看是否存在，不看领取状态）。
 *
 * domain 透传：dev 用 ?domain= 模拟时需把 domain 带给 BFF（proxy 据此注入 x-hotel-domain，
 * callUpstream 才能按域名解析 tenant）；生产走子域名，BFF 用 host 头解析。与 customer-service.ts 一致。
 *
 * 模块级缓存 + 并发去重：券包活动会话内稳定，缓存命中避免 datebar 切日期重渲染时重复打 BFF。
 * 仅 HTTP 200 才缓存（含无活动/无图也缓存）；瞬时失败（网络/非 200，含 404 券包不存在）不缓存、
 * 返回 null（fail-closed=不展示）。
 */
import type { GroupDetail, ClaimCouponResponse } from '@/types/coupon';
import { getLastDomain, readUserStorage } from '@/lib/shared-storage';
import { getConfig } from '../config';
import { get, post } from './client';
import { SdkError } from '../errors';

const BFF_PATH = '/api/coupons/group-detail';
const REQUEST_TIMEOUT = 15000;

/** 当前页面 domain（dev 透传 ?domain= 给 BFF；生产靠 host 头）--与 customer-service.ts 一致 */
function resolveBffDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const queryDomain = new URLSearchParams(window.location.search).get('domain');
  if (queryDomain) return queryDomain;
  return getLastDomain() || undefined;
}

/** 模块级缓存：cacheKey(含 domain) -> 券包详情（含空 coupons 的详情）；会话内稳定 */
const detailCache = new Map<string, GroupDetail | null>();
/** 并发去重锁：同 cacheKey 多实例/多请求共享同一次 fetch */
const inflight = new Map<string, Promise<GroupDetail | null>>();

/**
 * 查询券包详情（存在性校验 + Banner 图用）。
 * @param code 券包 code（如 homepage / summer_2026）
 * @returns 券包详情；无活动/异常返回 null（调用方据此不渲染 banner）
 */
export async function fetchGroupDetail(code: string): Promise<GroupDetail | null> {
  const domain = resolveBffDomain();
  const cacheKey = `${domain ?? '_'}:${code}`;

  if (detailCache.has(cacheKey)) return detailCache.get(cacheKey) ?? null;

  const existing = inflight.get(cacheKey);
  if (existing) return existing;

  const p = (async (): Promise<GroupDetail | null> => {
    try {
      const { basePath } = getConfig();
      const params = new URLSearchParams({ code });
      if (domain) params.set('domain', domain);
      const url = `${basePath}${BFF_PATH}?${params.toString()}`;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
      let res: Response;
      try {
        res = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      if (!res.ok) return null; // 非 200（含 404 券包不存在）：fail-closed，不缓存
      const data = (await res.json()) as unknown;
      // 轻校验：需为带 coupons 数组的对象，否则视为异常
      if (!data || typeof data !== 'object' || !Array.isArray((data as GroupDetail).coupons)) {
        return null;
      }
      const detail = data as GroupDetail;
      detailCache.set(cacheKey, detail); // 含空 coupons（无活动）也缓存
      return detail;
    } catch {
      return null; // 网络/解析异常：fail-closed，不缓存
    } finally {
      inflight.delete(cacheKey);
    }
  })();

  inflight.set(cacheKey, p);
  return p;
}

/**
 * 读取宿主页面 URL 的 ?code=（营销投放券组 code）。
 * 无则返回 undefined（调用方回退 homepage）--镜像主站 PackagesPage 的 searchParams.code
 * -> CouponClaimEntry.couponGroupCode 的解析链路。
 */
export function readCouponGroupCode(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const code = new URLSearchParams(window.location.search).get('code');
  return code?.trim() || undefined;
}

/**
 * 券包详情查询(走 sdk client 同源 BFF /api/coupons/group-detail)。
 * 公开端点;一次返回券包基本信息(Banner/头图)+ coupons 嵌入券列表。登录后传 phone
 * (member_phone)拿每张券的领取状态(claimed/issue_status),供红包弹框列券 + 标已领 + 取头图
 * (head_image_pc_url/head_image_mobile_url)。与 fetchGroupDetail 的区别:本函数传 phone 拿领取态、走
 * sdk client(弹框按需请求,不复用存在性校验的模块缓存)。
 * @param code 券包 code(如 homepage / summer_2026)
 * @param phone 登录用户手机号,未登录不传(领取状态字段返回 null)
 */
export async function getGroupDetail(code: string, phone?: string): Promise<GroupDetail> {
  const params: Record<string, unknown> = { code };
  if (phone) params.phone = phone;
  return get<GroupDetail>('/api/coupons/group-detail', params);
}

/**
 * 领取优惠券(走 sdk client 同源 BFF /api/coupons/claim;需登录,client 自动带 Authorization)。
 * phone 从 user-storage 读(登录后 writeUserStorage 写入);未登录抛 401(正常流程由 openLogin 门禁,
 * 不到此处)。
 * @param couponCode 券模板 code(来自 GroupCoupon.code)
 */
export async function claimCoupon(couponCode: string): Promise<ClaimCouponResponse> {
  const phone = readUserStorage()?.user?.mobile;
  if (!phone) throw new SdkError(401, '未登录');
  const url = `/api/coupons/claim?phone=${encodeURIComponent(phone)}`;
  return post<ClaimCouponResponse>(url, { coupon_code: couponCode });
}
